import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ShieldCheck, UserCheck, CircleDashed, Info } from "lucide-react";
import { ago, label } from "@/lib/api";

const EVIDENCE = {
  confirmed: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: ShieldCheck, text: "Confirmed submission" },
  candidate_reported: { cls: "bg-amber-50 text-amber-800 border-amber-200", icon: UserCheck, text: "Candidate-reported" },
  unconfirmed: { cls: "bg-slate-100 text-slate-600 border-slate-200", icon: CircleDashed, text: "Unconfirmed" },
};

export const EvidenceBadge = ({ value, testid }) => {
  const e = EVIDENCE[value] || EVIDENCE.unconfirmed;
  const Icon = e.icon;
  return (
    <span data-testid={testid || `evidence-${value}`} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${e.cls}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden /> {e.text}
    </span>
  );
};

const BAND = { strong: "text-emerald-700 bg-emerald-50 border-emerald-200", good: "text-blue-700 bg-blue-50 border-blue-200",
  partial: "text-amber-800 bg-amber-50 border-amber-200", low: "text-slate-600 bg-slate-100 border-slate-200" };

export const MatchBreakdown = ({ match }) => (
  <div className="space-y-3" data-testid="match-breakdown">
    {match.components.map((c) => (
      <div key={c.factor}>
        <div className="flex justify-between text-sm"><span className="font-medium">{c.factor}</span><span className="font-mono">{c.points}/{c.max}</span></div>
        <div className="mt-1 h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-primary" style={{ width: `${(c.points / c.max) * 100}%` }} /></div>
        <p className="mt-1 text-xs text-muted-foreground">{c.reason}</p>
      </div>
    ))}
    {match.mandatory_flags?.length > 0 && (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="match-mandatory-flags">
        <p className="font-semibold">Mandatory qualification checks (score capped at 60)</p>
        {match.mandatory_flags.map((f) => <p key={f}>• {f}</p>)}
      </div>
    )}
    <p className="text-[11px] text-muted-foreground border-t pt-2">{match.disclaimer}</p>
  </div>
);

export const MatchBadge = ({ match, testid }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button data-testid={testid || "match-score-button"} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-semibold transition-colors hover:brightness-95 ${BAND[match.band]}`}>
        <span className="font-mono">{match.score}</span><span className="text-xs font-medium">fit</span><Info className="h-3.5 w-3.5" aria-hidden />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-80 bg-white"><MatchBreakdown match={match} /></PopoverContent>
  </Popover>
);

const LINK_NOTE = { broken: ["· link failing", "text-red-600"], expired: ["· posting closed", "text-red-600"], inconclusive: ["· link check inconclusive", "text-amber-600"], awaiting: ["· link check pending", "text-slate-500"] };

export const Freshness = ({ job }) => (
  <span className="text-xs text-muted-foreground" data-testid="job-freshness">
    {label(job.source) === "platform" ? "Posted on JobPedestal" : `via ${job.provenance?.board || job.source}`} · verified {ago(job.last_verified_at)}
    {LINK_NOTE[job.link_status] && <span className={`ml-1 font-medium ${LINK_NOTE[job.link_status][1]}`} data-testid="job-link-status">{LINK_NOTE[job.link_status][0]}</span>}
  </span>
);

export const StatusPill = ({ status }) => {
  const map = { "Live and Tested": "bg-emerald-50 text-emerald-700 border-emerald-200", "Implemented but Unverified": "bg-blue-50 text-blue-700 border-blue-200",
    "Requires Credentials or Authorization": "bg-amber-50 text-amber-800 border-amber-200", "Partially Implemented": "bg-violet-50 text-violet-700 border-violet-200",
    "Not Implemented": "bg-slate-100 text-slate-600 border-slate-200" };
  return <Badge variant="outline" className={`whitespace-nowrap font-medium ${map[status] || ""}`}>{status}</Badge>;
};

export const PageHeader = ({ eyebrow, title, children, testid }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between fade-up">
    <div>
      {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
      <h1 data-testid={testid || "page-title"} className="text-3xl sm:text-4xl font-extrabold tracking-tight">{title}</h1>
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

export const Panel = ({ children, className = "", testid }) => (
  <section data-testid={testid} className={`rounded-2xl border bg-card p-6 shadow-sm ${className}`}>{children}</section>
);

export const Stat = ({ labelText, value, testid, hint }) => (
  <div className="rounded-xl border bg-card p-5" data-testid={testid}>
    <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{labelText}</p>
    <p className="mt-2 font-display text-3xl font-bold">{value}</p>
    {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
  </div>
);
